import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiCheckCircle, 
  FiClock, 
  FiCircle, 
  FiChevronDown, 
  FiArrowRight, 
  FiTrendingUp, 
  FiTarget, 
  FiZap,
  FiAward
} = FiIcons;

const StatusSelector = ({ status, isTeamOwner, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const options = [
    { value: 0, label: 'OFFEN', icon: FiCircle, color: 'text-gray-500', bg: 'bg-gray-100', border: 'border-gray-200' },
    { value: 1, label: 'IN PROGRESS', icon: FiClock, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
    { value: 2, label: 'UMGESETZT', icon: FiCheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' }
  ];

  const currentOption = options.find(o => 
    (status === 'open' && o.value === 0) ||
    (status === 'in_progress' && o.value === 1) ||
    (status === 'implemented' && o.value === 2)
  ) || options[0];

  if (!isTeamOwner) {
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide border ${currentOption.bg} ${currentOption.color} ${currentOption.border} cursor-default select-none`}>
        <SafeIcon icon={currentOption.icon} className="w-4 h-4" />
        {currentOption.label}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide border transition-all ${currentOption.bg} ${currentOption.color} ${currentOption.border} hover:shadow-sm`}
      >
        <SafeIcon icon={currentOption.icon} className="w-4 h-4" />
        {currentOption.label}
        <SafeIcon icon={FiChevronDown} className={`w-3 h-3 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-44 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-30 animate-fadeIn">
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={(e) => {
                e.stopPropagation();
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-xs font-semibold flex items-center gap-3 transition-colors ${
                currentOption.value === opt.value 
                  ? 'bg-brand-primary/5 text-brand-secondary' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={opt.icon} className={`w-4 h-4 ${opt.color}`} />
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const IntelligentCard = ({ rec, isTeamOwner, onStatusChange, rank }) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 relative group hover:z-20">
      {/* Rank Badge */}
      <div className="absolute top-0 right-0 p-4">
         <div className="bg-gradient-to-br from-brand-secondary to-brand-primary text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
           #{rank}
         </div>
      </div>

      <div className="p-6">
        <div className="flex items-start gap-4 mb-4 pr-12">
           <div className="p-3 bg-brand-primary/5 rounded-lg text-brand-secondary">
             <SafeIcon icon={FiZap} className="w-6 h-6" />
           </div>
           <div>
             <h3 className="text-lg font-bold text-gray-900 leading-tight mb-2">{rec.title}</h3>
             <div className="flex items-center gap-3 text-sm text-gray-500">
               <span className="flex items-center gap-1">
                 <SafeIcon icon={FiTarget} className="w-4 h-4" />
                 Beeinflusst {rec.questionCount} {rec.questionCount === 1 ? 'Bereich' : 'Bereiche'}
               </span>
               <span className="flex items-center gap-1 font-semibold text-brand-secondary">
                  <SafeIcon icon={FiTrendingUp} className="w-4 h-4" />
                  Impact Score: {rec.impactScore.toFixed(1)}
               </span>
             </div>
           </div>
        </div>

        <p className="text-gray-600 text-sm mb-6 line-clamp-3">
          {rec.short_description || rec.description || "Keine Beschreibung verfügbar."}
        </p>

        {/* Impact Analysis - Show ALL areas */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-100">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Alle Einflussbereiche</h4>
          <div className="space-y-2">
            {rec.affectedQuestions.map((q, idx) => (
              <div key={idx} className="flex justify-between items-center border-b border-gray-100 last:border-0 pb-2 last:pb-0">
                <span className="text-sm font-medium text-gray-700 truncate mr-4 flex-1" title={q.text}>
                  {q.text}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                   <div className="text-xs text-gray-400">Ø Score</div>
                   <div className={`px-2 py-1 rounded text-xs font-bold min-w-[36px] text-center ${
                     q.averageScore >= 6 ? 'bg-green-100 text-green-700' :
                     q.averageScore >= 4 ? 'bg-yellow-100 text-yellow-700' :
                     'bg-red-100 text-red-700'
                   }`}>
                     {q.averageScore ? q.averageScore.toFixed(1) : '-'}
                   </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between items-center pt-2 border-t border-gray-50">
           <StatusSelector 
              status={rec.status} 
              isTeamOwner={isTeamOwner} 
              onChange={(newStatus) => onStatusChange(rec.id, newStatus, rec.relationId)} 
            />
            
            <button 
              onClick={() => navigate(`/handbook/recommendation/${rec.id}`)}
              className="text-sm font-semibold text-brand-secondary hover:text-brand-dark flex items-center transition-colors group"
            >
              Details
              <SafeIcon icon={FiArrowRight} className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
        </div>
      </div>
      
      {/* Decorative gradient bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-brand-secondary/40 to-brand-primary/40 absolute bottom-0 left-0 rounded-b-xl" />
    </div>
  );
};

const IntelligentHandbook = ({ analyzedQuestions, isTeamOwner, onStatusChange }) => {
  const topRecommendations = useMemo(() => {
    const recMap = new Map();

    analyzedQuestions.forEach(q => {
      if (!q.recommendations) return;

      q.recommendations.forEach(rec => {
        if (rec.status === 'implemented' || rec.status === 'in_progress') return;

        if (!recMap.has(rec.id)) {
          recMap.set(rec.id, {
            ...rec,
            affectedQuestions: [],
            questionCount: 0
            // impactScore is already present in rec from Handbook.jsx
          });
        }

        const entry = recMap.get(rec.id);
        
        // Add question to affected list if not present
        if (!entry.affectedQuestions.some(aq => aq.id === q.id)) {
           entry.affectedQuestions.push(q);
           entry.questionCount += 1;
        }
      });
    });

    return Array.from(recMap.values())
      .sort((a, b) => b.impactScore - a.impactScore)
      .slice(0, 3);
  }, [analyzedQuestions]);

  if (topRecommendations.length === 0) {
    return (
      <div className="bg-white rounded-xl p-8 text-center border border-gray-200 shadow-sm animate-fadeIn">
        <div className="bg-green-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiAward} className="w-10 h-10 text-green-500" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Alles im grünen Bereich!</h3>
        <p className="text-gray-600 max-w-lg mx-auto">
          Aktuell gibt es keine dringenden Handlungsempfehlungen für dein Team. 
          Ihr habt entweder alle Empfehlungen umgesetzt oder eure Bewertungen sind hervorragend.
        </p>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="mb-8 text-center max-w-2xl mx-auto">
        <h2 className="text-xl font-bold text-brand-dark mb-2">Deine Top 3 Handlungsempfehlungen</h2>
        <p className="text-gray-600">
          Diese Auswahl basiert auf einer intelligenten Analyse eurer Team-Bewertungen. 
          Diese Maßnahmen versprechen den größten positiven Einfluss auf eure Zusammenarbeit.
        </p>
      </div>

      <div className="flex flex-col gap-6">
        {topRecommendations.map((rec, index) => (
          <IntelligentCard 
            key={rec.id} 
            rec={rec} 
            rank={index + 1}
            isTeamOwner={isTeamOwner}
            onStatusChange={onStatusChange}
          />
        ))}
      </div>
    </div>
  );
};

export default IntelligentHandbook;